import styled, { css } from 'styled-components';
import { LayoutConst } from 'core';
import { RectangularButtonFilled, RectangularButtonHollow } from '@brg/ui';

export const LongreadBlockWrapper = styled.div<{ $mobilePadding?: string }>`
  width: 100%;
  height: ${({ $mobilePadding }) => $mobilePadding ? 'fit-content' : '100%'};
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const TextBlock = styled.div<{ textColor?: string }>`
  width: 100%;
  color: ${({ textColor }) => textColor || '#151514'};
  font-size: 18px;
  line-height: 1.6;
  word-break: break-word;
  
  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 16px;
    line-height: 1.5;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 14px;
    line-height: 20px;
  }
    
  li {
    margin-bottom: 8px;
    color: ${({ textColor }) => textColor || '#151514'};
  }
  
  a {
    color: ${({ textColor }) => textColor || '#151514'};
  }
`;

export const NormalBlockWrapper = styled.div<{ $background?: string; $padding?: string; $mobilePadding?: string }>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: ${({ $background }) => $background || 'rgba(21, 21, 20, 1)'};
  overflow-y: auto;
  padding: 40px 0;
  box-sizing: border-box;

  @media (max-width: ${LayoutConst.screen.m}px) {
    align-items: flex-start;
    padding: 16px 10px 48px 10px;
  }

  ${({ $padding }) => $padding && css`padding: ${$padding};`}
  @media (max-width: ${LayoutConst.screen.s}px) {
    ${({ $mobilePadding }) => $mobilePadding && css`padding: ${$mobilePadding};`}
  }
`;

export const NormalTextBlock = styled.div<{ textColor?: string; fontSize?: number}>`
  width: 50%;
  margin-left: auto;
  margin-top: auto;
  margin-bottom: auto;
  font-size: ${({ fontSize }) => fontSize || 24}px;
  font-weight: 400;
  padding: 40px 120px 40px 0;
  line-height: 115%;
  letter-spacing: -0.4px;
  color: ${({ textColor }) => textColor || '#FEFEFE'};
  white-space: pre-line;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: ${({ fontSize }) => {
      if (fontSize === 17) return '15px';
      if (fontSize === 19) return '17px';
      return '24px';
    }};
    line-height: 120%;
    letter-spacing: -0.36px;
    padding: 16px;
  }
  @media (max-width: ${LayoutConst.screen.m}px) {
    width: 100%;
    font-size: ${({ fontSize }) => {
      if (fontSize === 17) return '14px';
      if (fontSize === 19) return '16px';
      return '20px';
    }};
    letter-spacing: -0.24px;
    padding: 0 10px;
  }
  
  ul {
    margin: 0;
    padding-left: 1.2em;
    white-space: normal;
  }

  ol {
    margin: 0;
    padding-left: 0;
    list-style-position: inside;
    white-space: normal;
  }

  li {
    margin-bottom: 0;
    color: ${({ textColor }) => textColor || '#FEFEFE'};
  }

  li + li {
    margin-top: 8px;
  }

  ol li {
    padding-left: 0;
    white-space: normal;
  }

  li > p {
    margin: 0;
  }
  
  a {
    color: ${({ textColor }) => textColor || '#FEFEFE'};
  }

  h1 {
    line-height: 105%;
  }
`;

export const RightTextBlock = styled(NormalTextBlock)<{textLength: number}>`
    width: 100%;
    font-size: 20px;
    line-height: 1.3;
    @media (max-width: ${LayoutConst.screen.m}px) {
      font-size: 15px;
      line-height: 17px;
      height: auto;
      padding: 0 16px;
      & h1 {
        font-size: 20px!important;
        line-height: 24px!important;;
      }
    }
    @media (max-width: ${LayoutConst.screen.s}px) {
      font-size: 13px;
      line-height: 15px;
      & h1 {
        font-size: 17px!important;
        line-height: 20px!important;;
      }
    }
`

export const ImageBlock = styled.div<{isVerticalImage?: boolean; isWideImage?: boolean}>`
  width: 100%;
  height: 100%;
  margin: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    padding: 12px;
    box-sizing: border-box;

    img {
      object-fit: contain !important;
      max-width: 100%;
      max-height: 100%;
    }
  }
`;

export const ColoredBlock = styled.div<{ backgroundColor?: string; textColor?: string; $padding?: string; $mobilePadding?: string }>`
  width: 100%;
  height: 100%;
  padding: 48px 0;
  background: ${({ backgroundColor }) => backgroundColor || '#FEFEFE'};
  color: ${({ textColor }) => textColor || '#151514'};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    padding: ${({ $padding }) => $padding ? $padding : '36px 0'};
  }
  @media (max-width: ${LayoutConst.screen.md}px) {
    padding: 24px 12px;
  }

  ${({ $padding }) => $padding && css`padding: ${$padding};`}
  @media (max-width: ${LayoutConst.screen.s}px) {
    ${({ $mobilePadding }) => $mobilePadding && css`padding: ${$mobilePadding};`}
  }
`;

export const ColoredTextBlock = styled.div<{ fontSchema?: any,textColor?: string; fontSize?: number }>`
  width: 100%;
  font-size: ${({ fontSize }) => fontSize || 42}px;
  font-weight: 600;
  line-height: 115%;
  letter-spacing: ${({ fontSize }) => fontSize === 32 ? '-0.64px' : '-0.84px'};
  color: ${({ textColor }) => textColor || '#151514'};
  overflow: hidden;
  white-space: pre-line;

  ${({ fontSchema }) => fontSchema && css`${fontSchema};`};

  h1 {
    margin-top: 0.67em;
    margin-bottom: 0.67em;
    line-height: 100%;
  }
  
  h1 {
    margin-top: 0.67em;
    margin-bottom: 0.67em;
    line-height: 100%;
  }
  
  ul,
  ol {
    margin: 0;
    padding-left: 1.2em;
  }

  li {
    margin-bottom: 0;
    color: ${({ textColor }) => textColor || '#151514'};
  }

  li + li {
    margin-top: 8px;
  }

  li > p {
    margin: 0;
  }
  
  a {
    color: ${({ textColor }) => textColor || '#151514'};
  }

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    // max-width: 900px;
    font-size: 28px;
    letter-spacing: -0.56px;
     h1 {
      margin-top: 0;
      margin-bottom: 0;
    }
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    max-width: none;
    font-size: 20px;
    line-height: 125%;
    letter-spacing: -0.2px;
  }
`;

export const ColoredCaptionText = styled.div<{ textColor?: string; promoteToMainText?: boolean }>`
  width: 100%;
  font-size: ${({ promoteToMainText }) => (promoteToMainText ? '28px' : '15px')};
  font-weight: ${({ promoteToMainText }) => (promoteToMainText ? 600 : 400)};
  line-height: ${({ promoteToMainText }) => (promoteToMainText ? '1.3' : '1.5')};
  color: ${({ textColor }) => textColor || '#151514'};
  white-space: pre-line;
  word-break: break-word;

  p {
    margin: 0;
  }

  a {
    color: ${({ textColor }) => textColor || '#151514'};
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: ${({ promoteToMainText }) => (promoteToMainText ? '20px' : '13px')};
    line-height: ${({ promoteToMainText }) => (promoteToMainText ? '125%' : '15px')};
    letter-spacing: ${({ promoteToMainText }) => (promoteToMainText ? '-0.2px' : 'normal')};
  }
`;

export const BigImageBlock = styled.div<{ $mobilePadding?: string }>`
  width: 100%;
  height: 100%;
  margin: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    padding: ${({ $mobilePadding }) => $mobilePadding ? 0 : '12px'};
    box-sizing: border-box;

    img {
      max-width: 100%;
      max-height: 100%;
    }
  }
`;

export const ImageTextWrapper = styled.div<{ position?: string }>`
  position: absolute;
  width: 100%;
  z-index: 2;
  padding: 24px;
  box-sizing: border-box;
  
  ${({ position }) => {
    switch (position) {
      case 'top':
        return `
          top: 40px;
          left: 0;
          right: 0;
        `;
      case 'center':
        return `
          top: 50%;
          left: 0;
          right: 0;
          transform: translateY(-50%);
        `;
      case 'bottom':
      default:
        return `
          bottom: 0px;
          left: 0;
          right: 0;
        `;
    }
  }}

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    padding: 20px;
    ${({ position }) => {
      if (position === 'top') {
        return `
          top: 28px;
        `;
      }
      return '';
    }}
  }
`;

export const ImageTextContent = styled.div<{ textColor?: string }>`
  color: ${({ textColor }) => textColor || '#FEFEFE'};
  font-size: 42px;
  font-weight: 600;
  line-height: 115%;
  letter-spacing: -0.84px;
  
  p {
    margin: 0;
  }

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 34px;
    letter-spacing: -0.68px;
  }
`;

export const ImageTextTwoPicturesPrint = styled.div<{ position?: string }>`
  position: absolute;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 5;
  color: #fefefe;
  word-break: break-word;
  font-size: 30px;
  font-style: normal;
  font-weight: 600;
  line-height: 40px;
  letter-spacing: -0.52px;
  box-sizing: border-box;

  a {
    color: inherit;
  }

  p {
    margin: 0;
  }

  padding: 36px 64px 36px 36px;

  ${({ position }) => {
    if (position === 'top') {
      return css`
        bottom: initial;
        top: 0;
      `;
    }

    if (position === 'center') {
      return css`
        bottom: initial;
        top: 50%;
        transform: translateY(-50%);
        text-align: center;
      `;
    }
  }}

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 24px;
    line-height: 32px;
    letter-spacing: -0.42px;
    padding: 24px 48px 24px 24px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 16px;
    line-height: 22px;
    letter-spacing: -0.2px;
    padding: 12px 16px;
  }
`;

export const ImageTextShadow = styled.div<{position?: string}>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1; 

  :before {
    content: ' ';
    width: 100%;
    height: 496px; 
    position: absolute;
    background: linear-gradient(
      180deg,
      rgba(38, 42, 45, 0) 0%,
      rgba(22, 24, 26, 0.6) 100%
    );
    mix-blend-mode: normal;
    bottom: 0;
    top: initial;

    ${({ position }) =>
      (position === 'top' || position === 'center') &&
      css`
        display: none;
      `}
  }

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    :before {
      height: 360px;
    }
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    :before {
      height: 220px;
    }
  }
`;

export const ImageTextPrint = styled.div<{ position?: string }>`
  position: absolute;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 5;
  color: #fefefe;
  word-break: break-word;
  font-size: 29px;
  font-style: normal;
  font-weight: 600;
  line-height: 39.5px;
  letter-spacing: -0.58px;
  max-width: none;
  box-sizing: border-box;

  a {
    color: inherit;
  }

  p {
    margin: 0;
  }

  ${({ position }) => {
    if (position === 'top') {
      return css`
        bottom: initial;
        top: 0;
        padding: 64px 144px 48px 64px;
      `;
    }

    if (position === 'center') {
      return css`
        bottom: initial;
        top: 50%;
        transform: translateY(-50%);
        text-align: center;
        padding: 48px 144px 48px 64px;
      `;
    }

    return css`
      padding: 48px 144px 64px 64px;
    `;
  }}

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 24px;
    line-height: 32px;
    letter-spacing: -0.48px;

    ${({ position }) => {
      if (position === 'top') {
        return css`
          padding: 40px 96px 32px 40px;
        `;
      }
  
      if (position === 'center') {
        return css`
          padding: 32px 96px 32px 40px;
        `;
      }
  
      return css`
        padding: 32px 96px 40px 40px;
      `;
    }}
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 18px;
    line-height: 24px;
    letter-spacing: -0.28px;

    ${({ position }) => {
      if (position === 'top') {
        return css`
          padding: 16px 16px 12px;
        `;
      }

      if (position === 'center') {
        return css`
          padding: 12px 16px;
        `;
      }

      return css`
        padding: 12px 16px 16px;
      `;
    }}
  }
`;

export const TwoPicturesPrintWrap = styled.div<{ withoutCaption?: boolean }>`
  position: relative;
  display: flex;
  height: 100%;
  width: 100%;
  max-width: 100%;
  margin: 0;
  box-sizing: border-box;
  overflow: hidden;
  flex-direction: column;
  justify-content: ${({ withoutCaption }) => withoutCaption ? 'center' : 'flex-start'};

  @media (max-width: ${LayoutConst.screen.s}px) {
    padding: 12px 12px 8px;
  }
`;

export const TwoPicturesList = styled.div<{ stackOnMobile?: boolean }>`
  position: relative;
  display: flex;
  gap: 24px;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    gap: 16px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    flex-direction: ${({ stackOnMobile }) => stackOnMobile ? 'column' : 'row'};
    gap: 12px;
  }
`;

export const TwoPicturesItem = styled.div<{ isHorizontal?: boolean; stackOnMobile?: boolean; ratio?: number }>`
  position: relative;
  overflow: hidden;
  width: 50%;
  height: ${({ isHorizontal }) => isHorizontal ? 'auto' : '100%'};
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: ${({ isHorizontal }) => isHorizontal ? 'contain' : 'cover'} !important;
  }

  ${({ stackOnMobile, ratio }) => stackOnMobile && css`
    width: auto;
    height: auto;
    min-width: 0;
    flex: ${ratio || 1} 1 0;

    img {
      width: 100%;
      height: auto;
      object-fit: contain !important;
    }
  `}

  @media (max-width: ${LayoutConst.screen.s}px) {
    ${({ stackOnMobile }) => stackOnMobile && `
      width: 100%;
      height: auto;
      flex: 0 0 auto;
    `}

    img {
      object-fit: contain !important;
      ${({ stackOnMobile }) => stackOnMobile && `
        width: 100%;
        height: auto;
      `}
    }
  }
`;

export const ImageCaption = styled.div<{ textColor?: string; overlayPosition?: string }>`
  position: absolute;
  bottom: ${({ overlayPosition }) => overlayPosition === 'bottom' ? '72px' : '0'};
  left: 50%;
  transform: translateX(-50%);
  padding: 16px;
  font-size: 14px;
  color: ${({ textColor }) => textColor || '#666'};
  line-height: 1.5;
  max-width: 1200px;
  width: 100%;
  text-align: center;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    bottom: ${({ overlayPosition }) => overlayPosition === 'bottom' ? '60px' : '0'};
    padding: 12px;
    font-size: 12px;
    line-height: 1.4;
    max-width: 900px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    bottom: ${({ overlayPosition }) => overlayPosition === 'bottom' ? '34px' : '0'};
    padding: 8px 10px;
    font-size: 11px;
    line-height: 14px;
    max-width: none;
  }
`;

export const BigImageDragWrap = styled.div<{ 
  isPrint?: boolean;
  isCenterBlock?: boolean;
  isVerticalImage?: boolean;
  withoutCaption?: boolean;
  isNotWideImage?: boolean;
}>`
  position: relative;
  display: flex;
  height: 100%;
  width: 100%;
  margin: 0;
  box-sizing: border-box;
  overflow: hidden;
  flex-direction: ${({ isNotWideImage }) => isNotWideImage ? 'row' : 'column'};
  justify-content: ${({ withoutCaption, isCenterBlock }) => 
    withoutCaption || isCenterBlock ? 'center' : 'flex-start'};
`;

export const BigImageCaptionBlock = styled.div<{
  isPrint?: boolean;
  isVerticalImage?: boolean;
  withoutCaption?: boolean;
  isNotWideImage?: boolean;
  isUltraWideImage?: boolean;
}>`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const PrintImageWrapper = styled.div<{
  isNotWideImage?: boolean;
  isUltraWideImage?: boolean;
  overTextPosition?: string;
  isVerticalImage?: boolean;
  withoutCaption?: boolean;
}>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain !important;
    max-height: 100%;
  }
`;

export const OverTextWrapper = styled.div<{ overTextPosition?: string }>`
  position: absolute;
  left: 0;
  top: 0;
  color: rgb(254, 254, 254);
  word-break: break-word;
  width: 100%;
  height: 100%;
  font-weight: 600;
  font-size: 42px;
  line-height: 48px;
  letter-spacing: -0.02em;
  padding: 64px 90px 64px 48px;
  box-sizing: border-box;
  display: flex;
  align-items: ${({ overTextPosition }) =>
    overTextPosition === 'bottom'
      ? 'flex-end'
      : overTextPosition === 'center'
      ? 'center'
      : 'flex-start'};
  z-index: 2;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 34px;
    line-height: 40px;
    padding: 48px 72px 48px 40px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 20px;
    line-height: 26px;
    padding: 16px;
  }
`;

export const CaptionBlockWrapper = styled.div<{ textColor?: string }>`
  width: 100%;
  padding: 20px;
  text-align: center;
  color: ${({ textColor }) => textColor || '#151514'};
  font-size: 14px;
  line-height: 1.5;
  flex-shrink: 0;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    padding: 16px;
    font-size: 13px;
    line-height: 1.4;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    padding: 10px;
    font-size: 12px;
    line-height: 16px;
  }
`;

export const TwoColumnWrapper = styled.div<{ $background?: string; $padding?: string; $mobilePadding?: string; $mobileColumn?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 80px;
  box-sizing: border-box;
  gap: 40px;
  background: ${({ $background }) => $background || '#151514'};

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    padding: 56px;
    gap: 28px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    justify-content: flex-start;
    padding: 12px;
    gap: 12px;
    ${({ $mobileColumn }) => $mobileColumn && css`flex-direction: column;`}
  }

  ${({ $padding }) => $padding && css`padding: ${$padding};`}
  @media (max-width: ${LayoutConst.screen.s}px) {
    ${({ $mobilePadding }) => $mobilePadding && css`padding: ${$mobilePadding};`}
  }
`;

export const LeftColumn = styled.div`
  flex: 0 0 50%;
  width: 50%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;

  @media (max-width: ${LayoutConst.screen.s}px) {
    width: 100%;
    flex: 0 0 52%;
    height: 52%;
    min-height: 220px;
  }
`;

export const ImageContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  > div {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  img {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
    display: block;
  }
`;

export const RightColumn = styled.div`
  flex: 0 0 50%;
  width: 50%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  overflow-y: auto;
  min-width: 0; /* Позволяет flex элементу сжиматься */

  @media (max-width: ${LayoutConst.screen.s}px) {
    width: 100%;
    flex: 1 1 auto;
    height: auto;
    justify-content: flex-start;
    padding-bottom: 8px;
  }
`;

export const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 24px;
  
  a {
    text-decoration: none;
    pointer-events: auto;
  }

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    gap: 12px;
    margin-top: 20px;
  }
`;
export const NormalFilledButton = styled(RectangularButtonFilled)`
  color: #151514 !important;
  background: #FFFFFF !important;
  border: 1px solid #FFFFFF !important;

  &:hover,
  &:active {
    color: #151514 !important;
    background: #FFFFFF !important;
    border: 1px solid #FFFFFF !important;
  }
`;

export const NormalHollowButton = styled(RectangularButtonHollow)`
  color: #FFFFFF !important;
  border: 1px solid #FFFFFF !important;
  background: transparent !important;

  &:hover,
  &:active {
    color: #FFFFFF !important;
    border: 1px solid #FFFFFF !important;
    background: transparent !important;
  }
`;

export const ProductsWrapper = styled.div`
  position: relative;
  display: flex;
  height: 100%;
  width: 100%;
  max-width: 100%;
  margin: 0;
  box-sizing: border-box;
  overflow: hidden;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 40px;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    padding: 32px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    justify-content: flex-start;
    padding: 12px;
    gap: 8px;
  }
`;

export const ProductsList = styled.div`
  display: flex;
  gap: 24px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  max-width: 100%;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    gap: 20px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    gap: 16px;
  }
`;

export const ProductCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 446px;
  height: 628px;
  flex-shrink: 0;
  cursor: pointer;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    width: 380px;
    height: 535px;
  }
  @media (max-width: ${LayoutConst.screen.m}px) {
    width: 320px;
    height: 480px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    width: 200px;
    height: 280px;
  }
`;

export const ProductImage = styled.div`
  background: rgba(34, 34, 34, 0.06);
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;


  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: top center;
    max-width: 100%;
    max-height: 100%;
  }
`;

export const ProductTitle = styled.div`
  margin-top: 12px;
  font-family: Inter;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.3px;
  color: #909090;
  word-break: break-word;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 14px;
    line-height: 18px;
    margin-top: 10px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 13px;
    line-height: 16px;
    margin-top: 8px;
  }
`;

export const ProductPrice = styled.div<{ textColor?: string }>`
  margin-top: 4px;
  font-family: Inter;
  font-size: 34px;
  font-style: normal;
  font-weight: 600;
  line-height: 41px;
  letter-spacing: -0.68px;
  color: ${({ textColor }) => textColor || '#FFF'};

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 28px;
    line-height: 34px;
    letter-spacing: -0.56px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 24px;
    line-height: 29px;
    letter-spacing: -0.48px;
    margin-top: 2px;
  }
`;

export const PodcastWrapper = styled.div`
  position: relative;
  display: flex;
  height: 100%;
  width: 100%;
  max-width: 100%;
  margin: 0;
  box-sizing: border-box;
  overflow: hidden;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 40px;
  background: #151514;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    padding: 32px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    padding: 24px;
    justify-content: flex-start;
  }
`;

export const PodcastContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  max-width: 1152px;
  gap: 40px;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    gap: 32px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 24px;
  }
`;

export const PodcastTitle = styled.div<{ textColor?: string }>`
  font-family: Inter;
  font-size: 48px;
  font-style: normal;
  font-weight: 600;
  line-height: 100%;
  letter-spacing: -0.96px;
  color: ${({ textColor }) => textColor || '#FEFEFE'};
  margin-bottom: 8px;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 40px;
    line-height: 48px;
    letter-spacing: -0.8px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 32px;
    line-height: 38px;
    letter-spacing: -0.64px;
    margin-bottom: 4px;
  }
`;

export const PodcastDuration = styled.div<{ textColor?: string }>`
  font-family: Inter;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: -0.4px;
  color: ${({ textColor }) => textColor || '#FEFEFE'};
  opacity: 0.8;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 18px;
    line-height: 24px;
    letter-spacing: -0.36px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 16px;
    line-height: 20px;
    letter-spacing: -0.32px;
  }
`;

export const PodcastListenButton = styled.button<{ textColor?: string }>`
  padding: 13px 48px;
  border: 1px solid ${({ textColor }) => textColor || '#FEFEFE'};
  background: none;
  color: ${({ textColor }) => textColor || '#FEFEFE'};
  font-family: Inter;
  font-size: 17px;
  font-weight: 600;
  line-height: 22px;
  letter-spacing: -0.34px;
  cursor: pointer;
  border-radius: 3px;
  transition: all 0.2s ease;
  white-space: nowrap;
  &:hover {
    opacity: 0.9;
  }

  &:active {
    opacity: 0.8;
  }

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    padding: 10px 28px;
    font-size: 15px;
    line-height: 19px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    padding: 8px 24px;
    font-size: 14px;
    line-height: 18px;
    width: 100%;
    max-width: 200px;
  }
`;

export const PodcastIframeWrapper = styled.div<{ height?: number }>`
  width: 100%;
  max-width: 100%;
  height: ${({ height }) => height || 300}px;
  display: flex;
  align-items: center;
  justify-content: center;

  iframe {
    width: 100%;
    max-width: 100%;
    border: 0;
    background: transparent;
  }

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    height: ${({ height }) => height ? Math.min(height, 250) : 250}px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    height: ${({ height }) => height ? Math.min(height, 200) : 200}px;
  }
`;

export const PodcastPlayer = styled.div<{ textColor?: string }>`
  width: 1152px;
  height: 48px;
  padding: 6px 32px;
  border-radius: 8px;
  background: #343434;
  margin-top: 32px;
  display: flex;
  align-items: center;
  box-sizing: border-box;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    width: 100%;
    max-width: 1000px;
    margin-top: 24px;
    padding: 6px 24px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    width: 100%;
    max-width: 100%;
    margin-top: 20px;
    padding: 6px 16px;
    height: auto;
    min-height: 48px;
  }
`;

export const PodcastPlayerControls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  height: 100%;

  @media (max-width: ${LayoutConst.screen.s}px) {
    gap: 12px;
  }
`;

export const PodcastPlayerButton = styled.button<{ textColor?: string }>`
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  color: #FEFEFE;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
  transition: all 0.2s ease;
  padding: 0;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    opacity: 0.6;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    width: 32px;
    height: 32px;
    font-size: 16px;
  }
`;

export const PodcastPlayerProgress = styled.div<{ textColor?: string }>`
  flex: 1;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  cursor: pointer;
  position: relative;
  overflow: hidden;

  @media (max-width: ${LayoutConst.screen.s}px) {
    height: 3px;
  }
`;

export const PodcastPlayerTime = styled.div<{ textColor?: string }>`
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  color: #FEFEFE;
  opacity: 0.9;
  white-space: nowrap;
  flex-shrink: 0;

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 12px;
    line-height: 16px;
  }
`;

export const ProjectsPrintWrap = styled.div`
  position: relative;
  display: flex;
  height: 100%;
  width: 100%;
  max-width: 100%;
  margin: 0;
  box-sizing: border-box;
  overflow: hidden;
  flex-direction: column;
  justify-content: center;

  @media (max-width: ${LayoutConst.screen.s}px) {
    justify-content: flex-start;
    padding: 12px;
    gap: 8px;
  }
`;

export const SliderWithCaptionWrap = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 0;
`;

export const SliderImageArea = styled.div`
  position: relative;
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
`;

export const SliderCaptionText = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`;

export const ProjectList = styled.div`
  display: flex;
  justify-content: center;
  overflow: hidden;
  column-gap: 24px;
  max-height: 100%;
  width: 100%;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    column-gap: 16px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    flex-direction: column;
    align-items: center;
    row-gap: 8px;
    overflow: auto;
    max-height: none;
  }
`;

export const OverlayTextBlock = styled(TextBlock)<{ hasCaption?: boolean }>`
  position: absolute;
  left: 20px;
  right: 20px;
  width: auto;
  bottom: ${({ hasCaption }) => hasCaption ? '60px' : '20px'};

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    left: 16px;
    right: 16px;
    bottom: ${({ hasCaption }) => hasCaption ? '48px' : '16px'};
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    left: 12px;
    right: 12px;
    bottom: ${({ hasCaption }) => hasCaption ? '34px' : '12px'};
  }
`;

export const ProjectItemWrapper = styled.a`
  width: 33%;
  height: 100%;
  position: relative;
  cursor: pointer;
  overflow: hidden;
  pointer-events: auto;
  z-index: 10;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    pointer-events: none;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    width: 100%;
    max-width: 420px;
    min-height: 150px;
    height: calc((100% - 16px) / 3);

    img {
      object-fit: contain;
      background: rgba(0, 0, 0, 0.04);
    }
  }
`;
import styled, { css } from 'styled-components';
import { LayoutConst } from 'core';
import { RectangularButtonFilled, RectangularButtonHollow } from '@brg/ui';

export const LongreadBlockWrapper = styled.div<{ $mobilePadding?: string }>`
  width: 100%;
  height: ${({ $mobilePadding }) => $mobilePadding ? 'fit-content' : '100%'};
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const TextBlock = styled.div<{ textColor?: string }>`
  width: 100%;
  color: ${({ textColor }) => textColor || '#151514'};
  font-size: 18px;
  line-height: 1.6;
  word-break: break-word;
  
  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 16px;
    line-height: 1.5;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 14px;
    line-height: 20px;
  }
    
  li {
    margin-bottom: 8px;
    color: ${({ textColor }) => textColor || '#151514'};
  }
  
  a {
    color: ${({ textColor }) => textColor || '#151514'};
  }
`;

export const NormalBlockWrapper = styled.div<{ $background?: string; $padding?: string; $mobilePadding?: string }>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: ${({ $background }) => $background || 'rgba(21, 21, 20, 1)'};
  overflow-y: auto;
  padding: 40px 0;
  box-sizing: border-box;

  @media (max-width: ${LayoutConst.screen.m}px) {
    align-items: flex-start;
    padding: 16px 10px 48px 10px;
  }

  ${({ $padding }) => $padding && css`padding: ${$padding};`}
  @media (max-width: ${LayoutConst.screen.s}px) {
    ${({ $mobilePadding }) => $mobilePadding && css`padding: ${$mobilePadding};`}
  }
`;

export const NormalTextBlock = styled.div<{ textColor?: string; fontSize?: number}>`
  width: 50%;
  margin-left: auto;
  margin-top: auto;
  margin-bottom: auto;
  font-size: ${({ fontSize }) => fontSize || 24}px;
  font-weight: 400;
  padding: 40px 120px 40px 0;
  line-height: 115%;
  letter-spacing: -0.4px;
  color: ${({ textColor }) => textColor || '#FEFEFE'};
  white-space: pre-line;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: ${({ fontSize }) => {
      if (fontSize === 17) return '15px';
      if (fontSize === 19) return '17px';
      return '24px';
    }};
    line-height: 120%;
    letter-spacing: -0.36px;
    padding: 16px;
  }
  @media (max-width: ${LayoutConst.screen.m}px) {
    width: 100%;
    font-size: ${({ fontSize }) => {
      if (fontSize === 17) return '14px';
      if (fontSize === 19) return '16px';
      return '20px';
    }};
    letter-spacing: -0.24px;
    padding: 0 10px;
  }
  
  ul {
    margin: 0;
    padding-left: 1.2em;
    white-space: normal;
  }

  ol {
    margin: 0;
    padding-left: 0;
    list-style-position: inside;
    white-space: normal;
  }

  li {
    margin-bottom: 0;
    color: ${({ textColor }) => textColor || '#FEFEFE'};
  }

  li + li {
    margin-top: 8px;
  }

  ol li {
    padding-left: 0;
    white-space: normal;
  }

  li > p {
    margin: 0;
  }
  
  a {
    color: ${({ textColor }) => textColor || '#FEFEFE'};
  }

  h1 {
    line-height: 105%;
  }
`;

export const RightTextBlock = styled(NormalTextBlock)<{textLength: number}>`
    width: 100%;
    font-size: 20px;
    line-height: 1.3;
    @media (max-width: ${LayoutConst.screen.m}px) {
      font-size: 15px;
      line-height: 17px;
      height: auto;
      padding: 0 16px;
      & h1 {
        font-size: 20px!important;
        line-height: 24px!important;;
      }
    }
    @media (max-width: ${LayoutConst.screen.s}px) {
      font-size: 13px;
      line-height: 15px;
      & h1 {
        font-size: 17px!important;
        line-height: 20px!important;;
      }
    }
`

export const ImageBlock = styled.div<{isVerticalImage?: boolean; isWideImage?: boolean}>`
  width: 100%;
  height: 100%;
  margin: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    padding: 12px;
    box-sizing: border-box;

    img {
      object-fit: contain !important;
      max-width: 100%;
      max-height: 100%;
    }
  }
`;

export const ColoredBlock = styled.div<{ backgroundColor?: string; textColor?: string; $padding?: string; $mobilePadding?: string }>`
  width: 100%;
  height: 100%;
  padding: 48px 0;
  background: ${({ backgroundColor }) => backgroundColor || '#FEFEFE'};
  color: ${({ textColor }) => textColor || '#151514'};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    padding: ${({ $padding }) => $padding ? $padding : '36px 0'};
  }
  @media (max-width: ${LayoutConst.screen.md}px) {
    padding: 24px 12px;
  }

  ${({ $padding }) => $padding && css`padding: ${$padding};`}
  @media (max-width: ${LayoutConst.screen.s}px) {
    ${({ $mobilePadding }) => $mobilePadding && css`padding: ${$mobilePadding};`}
  }
`;

export const ColoredTextBlock = styled.div<{ fontSchema?: any,textColor?: string; fontSize?: number }>`
  width: 100%;
  font-size: ${({ fontSize }) => fontSize || 42}px;
  font-weight: 600;
  line-height: 115%;
  letter-spacing: ${({ fontSize }) => fontSize === 32 ? '-0.64px' : '-0.84px'};
  color: ${({ textColor }) => textColor || '#151514'};
  overflow: hidden;
  white-space: pre-line;

  ${({ fontSchema }) => fontSchema && css`${fontSchema};`};

  h1 {
    margin-top: 0.67em;
    margin-bottom: 0.67em;
    line-height: 100%;
  }
  
  h1 {
    margin-top: 0.67em;
    margin-bottom: 0.67em;
    line-height: 100%;
  }
  
  ul,
  ol {
    margin: 0;
    padding-left: 1.2em;
  }

  li {
    margin-bottom: 0;
    color: ${({ textColor }) => textColor || '#151514'};
  }

  li + li {
    margin-top: 8px;
  }

  li > p {
    margin: 0;
  }
  
  a {
    color: ${({ textColor }) => textColor || '#151514'};
  }

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    // max-width: 900px;
    font-size: 28px;
    letter-spacing: -0.56px;
     h1 {
      margin-top: 0;
      margin-bottom: 0;
    }
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    max-width: none;
    font-size: 20px;
    line-height: 125%;
    letter-spacing: -0.2px;
  }
`;

export const ColoredCaptionText = styled.div<{ textColor?: string; promoteToMainText?: boolean }>`
  width: 100%;
  font-size: ${({ promoteToMainText }) => (promoteToMainText ? '28px' : '15px')};
  font-weight: ${({ promoteToMainText }) => (promoteToMainText ? 600 : 400)};
  line-height: ${({ promoteToMainText }) => (promoteToMainText ? '1.3' : '1.5')};
  color: ${({ textColor }) => textColor || '#151514'};
  white-space: pre-line;
  word-break: break-word;

  p {
    margin: 0;
  }

  a {
    color: ${({ textColor }) => textColor || '#151514'};
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: ${({ promoteToMainText }) => (promoteToMainText ? '20px' : '13px')};
    line-height: ${({ promoteToMainText }) => (promoteToMainText ? '125%' : '15px')};
    letter-spacing: ${({ promoteToMainText }) => (promoteToMainText ? '-0.2px' : 'normal')};
  }
`;

export const BigImageBlock = styled.div<{ $mobilePadding?: string }>`
  width: 100%;
  height: 100%;
  margin: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    padding: ${({ $mobilePadding }) => $mobilePadding ? 0 : '12px'};
    box-sizing: border-box;

    img {
      max-width: 100%;
      max-height: 100%;
    }
  }
`;

export const ImageTextWrapper = styled.div<{ position?: string }>`
  position: absolute;
  width: 100%;
  z-index: 2;
  padding: 24px;
  box-sizing: border-box;
  
  ${({ position }) => {
    switch (position) {
      case 'top':
        return `
          top: 40px;
          left: 0;
          right: 0;
        `;
      case 'center':
        return `
          top: 50%;
          left: 0;
          right: 0;
          transform: translateY(-50%);
        `;
      case 'bottom':
      default:
        return `
          bottom: 0px;
          left: 0;
          right: 0;
        `;
    }
  }}

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    padding: 20px;
    ${({ position }) => {
      if (position === 'top') {
        return `
          top: 28px;
        `;
      }
      return '';
    }}
  }
`;

export const ImageTextContent = styled.div<{ textColor?: string }>`
  color: ${({ textColor }) => textColor || '#FEFEFE'};
  font-size: 42px;
  font-weight: 600;
  line-height: 115%;
  letter-spacing: -0.84px;
  
  p {
    margin: 0;
  }

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 34px;
    letter-spacing: -0.68px;
  }
`;

export const ImageTextTwoPicturesPrint = styled.div<{ position?: string }>`
  position: absolute;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 5;
  color: #fefefe;
  word-break: break-word;
  font-size: 30px;
  font-style: normal;
  font-weight: 600;
  line-height: 40px;
  letter-spacing: -0.52px;
  box-sizing: border-box;

  a {
    color: inherit;
  }

  p {
    margin: 0;
  }

  padding: 36px 64px 36px 36px;

  ${({ position }) => {
    if (position === 'top') {
      return css`
        bottom: initial;
        top: 0;
      `;
    }

    if (position === 'center') {
      return css`
        bottom: initial;
        top: 50%;
        transform: translateY(-50%);
        text-align: center;
      `;
    }
  }}

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 24px;
    line-height: 32px;
    letter-spacing: -0.42px;
    padding: 24px 48px 24px 24px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 16px;
    line-height: 22px;
    letter-spacing: -0.2px;
    padding: 12px 16px;
  }
`;

export const ImageTextShadow = styled.div<{position?: string}>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1; 

  :before {
    content: ' ';
    width: 100%;
    height: 496px; 
    position: absolute;
    background: linear-gradient(
      180deg,
      rgba(38, 42, 45, 0) 0%,
      rgba(22, 24, 26, 0.6) 100%
    );
    mix-blend-mode: normal;
    bottom: 0;
    top: initial;

    ${({ position }) =>
      (position === 'top' || position === 'center') &&
      css`
        display: none;
      `}
  }

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    :before {
      height: 360px;
    }
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    :before {
      height: 220px;
    }
  }
`;

export const ImageTextPrint = styled.div<{ position?: string }>`
  position: absolute;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 5;
  color: #fefefe;
  word-break: break-word;
  font-size: 29px;
  font-style: normal;
  font-weight: 600;
  line-height: 39.5px;
  letter-spacing: -0.58px;
  max-width: none;
  box-sizing: border-box;

  a {
    color: inherit;
  }

  p {
    margin: 0;
  }

  ${({ position }) => {
    if (position === 'top') {
      return css`
        bottom: initial;
        top: 0;
        padding: 64px 144px 48px 64px;
      `;
    }

    if (position === 'center') {
      return css`
        bottom: initial;
        top: 50%;
        transform: translateY(-50%);
        text-align: center;
        padding: 48px 144px 48px 64px;
      `;
    }

    return css`
      padding: 48px 144px 64px 64px;
    `;
  }}

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 24px;
    line-height: 32px;
    letter-spacing: -0.48px;

    ${({ position }) => {
      if (position === 'top') {
        return css`
          padding: 40px 96px 32px 40px;
        `;
      }
  
      if (position === 'center') {
        return css`
          padding: 32px 96px 32px 40px;
        `;
      }
  
      return css`
        padding: 32px 96px 40px 40px;
      `;
    }}
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 18px;
    line-height: 24px;
    letter-spacing: -0.28px;

    ${({ position }) => {
      if (position === 'top') {
        return css`
          padding: 16px 16px 12px;
        `;
      }

      if (position === 'center') {
        return css`
          padding: 12px 16px;
        `;
      }

      return css`
        padding: 12px 16px 16px;
      `;
    }}
  }
`;

export const TwoPicturesPrintWrap = styled.div<{ withoutCaption?: boolean }>`
  position: relative;
  display: flex;
  height: 100%;
  width: 100%;
  max-width: 100%;
  margin: 0;
  box-sizing: border-box;
  overflow: hidden;
  flex-direction: column;
  justify-content: ${({ withoutCaption }) => withoutCaption ? 'center' : 'flex-start'};

  @media (max-width: ${LayoutConst.screen.s}px) {
    padding: 12px 12px 8px;
  }
`;

export const TwoPicturesList = styled.div<{ stackOnMobile?: boolean }>`
  position: relative;
  display: flex;
  gap: 24px;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    gap: 16px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    flex-direction: ${({ stackOnMobile }) => stackOnMobile ? 'column' : 'row'};
    gap: 12px;
  }
`;

export const TwoPicturesItem = styled.div<{ isHorizontal?: boolean; stackOnMobile?: boolean; ratio?: number }>`
  position: relative;
  overflow: hidden;
  width: 50%;
  height: ${({ isHorizontal }) => isHorizontal ? 'auto' : '100%'};
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: ${({ isHorizontal }) => isHorizontal ? 'contain' : 'cover'} !important;
  }

  ${({ stackOnMobile, ratio }) => stackOnMobile && css`
    width: auto;
    height: auto;
    min-width: 0;
    flex: ${ratio || 1} 1 0;

    img {
      width: 100%;
      height: auto;
      object-fit: contain !important;
    }
  `}

  @media (max-width: ${LayoutConst.screen.s}px) {
    ${({ stackOnMobile }) => stackOnMobile && `
      width: 100%;
      height: auto;
      flex: 0 0 auto;
    `}

    img {
      object-fit: contain !important;
      ${({ stackOnMobile }) => stackOnMobile && `
        width: 100%;
        height: auto;
      `}
    }
  }
`;

export const ImageCaption = styled.div<{ textColor?: string; overlayPosition?: string }>`
  position: absolute;
  bottom: ${({ overlayPosition }) => overlayPosition === 'bottom' ? '72px' : '0'};
  left: 50%;
  transform: translateX(-50%);
  padding: 16px;
  font-size: 14px;
  color: ${({ textColor }) => textColor || '#666'};
  line-height: 1.5;
  max-width: 1200px;
  width: 100%;
  text-align: center;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    bottom: ${({ overlayPosition }) => overlayPosition === 'bottom' ? '60px' : '0'};
    padding: 12px;
    font-size: 12px;
    line-height: 1.4;
    max-width: 900px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    bottom: ${({ overlayPosition }) => overlayPosition === 'bottom' ? '34px' : '0'};
    padding: 8px 10px;
    font-size: 11px;
    line-height: 14px;
    max-width: none;
  }
`;

export const BigImageDragWrap = styled.div<{ 
  isPrint?: boolean;
  isCenterBlock?: boolean;
  isVerticalImage?: boolean;
  withoutCaption?: boolean;
  isNotWideImage?: boolean;
}>`
  position: relative;
  display: flex;
  height: 100%;
  width: 100%;
  margin: 0;
  box-sizing: border-box;
  overflow: hidden;
  flex-direction: ${({ isNotWideImage }) => isNotWideImage ? 'row' : 'column'};
  justify-content: ${({ withoutCaption, isCenterBlock }) => 
    withoutCaption || isCenterBlock ? 'center' : 'flex-start'};
`;

export const BigImageCaptionBlock = styled.div<{
  isPrint?: boolean;
  isVerticalImage?: boolean;
  withoutCaption?: boolean;
  isNotWideImage?: boolean;
  isUltraWideImage?: boolean;
}>`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const PrintImageWrapper = styled.div<{
  isNotWideImage?: boolean;
  isUltraWideImage?: boolean;
  overTextPosition?: string;
  isVerticalImage?: boolean;
  withoutCaption?: boolean;
}>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain !important;
    max-height: 100%;
  }
`;

export const OverTextWrapper = styled.div<{ overTextPosition?: string }>`
  position: absolute;
  left: 0;
  top: 0;
  color: rgb(254, 254, 254);
  word-break: break-word;
  width: 100%;
  height: 100%;
  font-weight: 600;
  font-size: 42px;
  line-height: 48px;
  letter-spacing: -0.02em;
  padding: 64px 90px 64px 48px;
  box-sizing: border-box;
  display: flex;
  align-items: ${({ overTextPosition }) =>
    overTextPosition === 'bottom'
      ? 'flex-end'
      : overTextPosition === 'center'
      ? 'center'
      : 'flex-start'};
  z-index: 2;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 34px;
    line-height: 40px;
    padding: 48px 72px 48px 40px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 20px;
    line-height: 26px;
    padding: 16px;
  }
`;

export const CaptionBlockWrapper = styled.div<{ textColor?: string }>`
  width: 100%;
  padding: 20px;
  text-align: center;
  color: ${({ textColor }) => textColor || '#151514'};
  font-size: 14px;
  line-height: 1.5;
  flex-shrink: 0;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    padding: 16px;
    font-size: 13px;
    line-height: 1.4;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    padding: 10px;
    font-size: 12px;
    line-height: 16px;
  }
`;

export const TwoColumnWrapper = styled.div<{ $background?: string; $padding?: string; $mobilePadding?: string; $mobileColumn?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 80px;
  box-sizing: border-box;
  gap: 40px;
  background: ${({ $background }) => $background || '#151514'};

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    padding: 56px;
    gap: 28px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    justify-content: flex-start;
    padding: 12px;
    gap: 12px;
    ${({ $mobileColumn }) => $mobileColumn && css`flex-direction: column;`}
  }

  ${({ $padding }) => $padding && css`padding: ${$padding};`}
  @media (max-width: ${LayoutConst.screen.s}px) {
    ${({ $mobilePadding }) => $mobilePadding && css`padding: ${$mobilePadding};`}
  }
`;

export const LeftColumn = styled.div`
  flex: 0 0 50%;
  width: 50%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;

  @media (max-width: ${LayoutConst.screen.s}px) {
    width: 100%;
    flex: 0 0 52%;
    height: 52%;
    min-height: 220px;
  }
`;

export const ImageContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  > div {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  img {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
    display: block;
  }
`;

export const RightColumn = styled.div`
  flex: 0 0 50%;
  width: 50%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  overflow-y: auto;
  min-width: 0; /* Позволяет flex элементу сжиматься */

  @media (max-width: ${LayoutConst.screen.s}px) {
    width: 100%;
    flex: 1 1 auto;
    height: auto;
    justify-content: flex-start;
    padding-bottom: 8px;
  }
`;

export const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 24px;
  
  a {
    text-decoration: none;
    pointer-events: auto;
  }

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    gap: 12px;
    margin-top: 20px;
  }
`;
export const NormalFilledButton = styled(RectangularButtonFilled)`
  color: #151514 !important;
  background: #FFFFFF !important;
  border: 1px solid #FFFFFF !important;

  &:hover,
  &:active {
    color: #151514 !important;
    background: #FFFFFF !important;
    border: 1px solid #FFFFFF !important;
  }
`;

export const NormalHollowButton = styled(RectangularButtonHollow)`
  color: #FFFFFF !important;
  border: 1px solid #FFFFFF !important;
  background: transparent !important;

  &:hover,
  &:active {
    color: #FFFFFF !important;
    border: 1px solid #FFFFFF !important;
    background: transparent !important;
  }
`;

export const ProductsWrapper = styled.div`
  position: relative;
  display: flex;
  height: 100%;
  width: 100%;
  max-width: 100%;
  margin: 0;
  box-sizing: border-box;
  overflow: hidden;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 40px;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    padding: 32px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    justify-content: flex-start;
    padding: 12px;
    gap: 8px;
  }
`;

export const ProductsList = styled.div`
  display: flex;
  gap: 24px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  max-width: 100%;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    gap: 20px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    gap: 16px;
  }
`;

export const ProductCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 446px;
  height: 628px;
  flex-shrink: 0;
  cursor: pointer;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    width: 380px;
    height: 535px;
  }
  @media (max-width: ${LayoutConst.screen.m}px) {
    width: 320px;
    height: 480px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    width: 200px;
    height: 280px;
  }
`;

export const ProductImage = styled.div`
  background: rgba(34, 34, 34, 0.06);
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;


  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: top center;
    max-width: 100%;
    max-height: 100%;
  }
`;

export const ProductTitle = styled.div`
  margin-top: 12px;
  font-family: Inter;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.3px;
  color: #909090;
  word-break: break-word;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 14px;
    line-height: 18px;
    margin-top: 10px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 13px;
    line-height: 16px;
    margin-top: 8px;
  }
`;

export const ProductPrice = styled.div<{ textColor?: string }>`
  margin-top: 4px;
  font-family: Inter;
  font-size: 34px;
  font-style: normal;
  font-weight: 600;
  line-height: 41px;
  letter-spacing: -0.68px;
  color: ${({ textColor }) => textColor || '#FFF'};

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 28px;
    line-height: 34px;
    letter-spacing: -0.56px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 24px;
    line-height: 29px;
    letter-spacing: -0.48px;
    margin-top: 2px;
  }
`;

export const PodcastWrapper = styled.div`
  position: relative;
  display: flex;
  height: 100%;
  width: 100%;
  max-width: 100%;
  margin: 0;
  box-sizing: border-box;
  overflow: hidden;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 40px;
  background: #151514;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    padding: 32px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    padding: 24px;
    justify-content: flex-start;
  }
`;

export const PodcastContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  max-width: 1152px;
  gap: 40px;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    gap: 32px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 24px;
  }
`;

export const PodcastTitle = styled.div<{ textColor?: string }>`
  font-family: Inter;
  font-size: 48px;
  font-style: normal;
  font-weight: 600;
  line-height: 100%;
  letter-spacing: -0.96px;
  color: ${({ textColor }) => textColor || '#FEFEFE'};
  margin-bottom: 8px;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 40px;
    line-height: 48px;
    letter-spacing: -0.8px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 32px;
    line-height: 38px;
    letter-spacing: -0.64px;
    margin-bottom: 4px;
  }
`;

export const PodcastDuration = styled.div<{ textColor?: string }>`
  font-family: Inter;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: -0.4px;
  color: ${({ textColor }) => textColor || '#FEFEFE'};
  opacity: 0.8;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 18px;
    line-height: 24px;
    letter-spacing: -0.36px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 16px;
    line-height: 20px;
    letter-spacing: -0.32px;
  }
`;

export const PodcastListenButton = styled.button<{ textColor?: string }>`
  padding: 13px 48px;
  border: 1px solid ${({ textColor }) => textColor || '#FEFEFE'};
  background: none;
  color: ${({ textColor }) => textColor || '#FEFEFE'};
  font-family: Inter;
  font-size: 17px;
  font-weight: 600;
  line-height: 22px;
  letter-spacing: -0.34px;
  cursor: pointer;
  border-radius: 3px;
  transition: all 0.2s ease;
  white-space: nowrap;
  &:hover {
    opacity: 0.9;
  }

  &:active {
    opacity: 0.8;
  }

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    padding: 10px 28px;
    font-size: 15px;
    line-height: 19px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    padding: 8px 24px;
    font-size: 14px;
    line-height: 18px;
    width: 100%;
    max-width: 200px;
  }
`;

export const PodcastIframeWrapper = styled.div<{ height?: number }>`
  width: 100%;
  max-width: 100%;
  height: ${({ height }) => height || 300}px;
  display: flex;
  align-items: center;
  justify-content: center;

  iframe {
    width: 100%;
    max-width: 100%;
    border: 0;
    background: transparent;
  }

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    height: ${({ height }) => height ? Math.min(height, 250) : 250}px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    height: ${({ height }) => height ? Math.min(height, 200) : 200}px;
  }
`;

export const PodcastPlayer = styled.div<{ textColor?: string }>`
  width: 1152px;
  height: 48px;
  padding: 6px 32px;
  border-radius: 8px;
  background: #343434;
  margin-top: 32px;
  display: flex;
  align-items: center;
  box-sizing: border-box;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    width: 100%;
    max-width: 1000px;
    margin-top: 24px;
    padding: 6px 24px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    width: 100%;
    max-width: 100%;
    margin-top: 20px;
    padding: 6px 16px;
    height: auto;
    min-height: 48px;
  }
`;

export const PodcastPlayerControls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  height: 100%;

  @media (max-width: ${LayoutConst.screen.s}px) {
    gap: 12px;
  }
`;

export const PodcastPlayerButton = styled.button<{ textColor?: string }>`
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  color: #FEFEFE;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
  transition: all 0.2s ease;
  padding: 0;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    opacity: 0.6;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    width: 32px;
    height: 32px;
    font-size: 16px;
  }
`;

export const PodcastPlayerProgress = styled.div<{ textColor?: string }>`
  flex: 1;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  cursor: pointer;
  position: relative;
  overflow: hidden;

  @media (max-width: ${LayoutConst.screen.s}px) {
    height: 3px;
  }
`;

export const PodcastPlayerTime = styled.div<{ textColor?: string }>`
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  color: #FEFEFE;
  opacity: 0.9;
  white-space: nowrap;
  flex-shrink: 0;

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 12px;
    line-height: 16px;
  }
`;

export const ProjectsPrintWrap = styled.div`
  position: relative;
  display: flex;
  height: 100%;
  width: 100%;
  max-width: 100%;
  margin: 0;
  box-sizing: border-box;
  overflow: hidden;
  flex-direction: column;
  justify-content: center;

  @media (max-width: ${LayoutConst.screen.s}px) {
    justify-content: flex-start;
    padding: 12px;
    gap: 8px;
  }
`;

export const SliderWithCaptionWrap = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 0;
`;

export const SliderImageArea = styled.div`
  position: relative;
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
`;

export const SliderCaptionText = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`;

export const ProjectList = styled.div`
  display: flex;
  justify-content: center;
  overflow: hidden;
  column-gap: 24px;
  max-height: 100%;
  width: 100%;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    column-gap: 16px;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    flex-direction: column;
    align-items: center;
    row-gap: 8px;
    overflow: auto;
    max-height: none;
  }
`;

export const OverlayTextBlock = styled(TextBlock)<{ hasCaption?: boolean }>`
  position: absolute;
  left: 20px;
  right: 20px;
  width: auto;
  bottom: ${({ hasCaption }) => hasCaption ? '60px' : '20px'};

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    left: 16px;
    right: 16px;
    bottom: ${({ hasCaption }) => hasCaption ? '48px' : '16px'};
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    left: 12px;
    right: 12px;
    bottom: ${({ hasCaption }) => hasCaption ? '34px' : '12px'};
  }
`;

export const ProjectItemWrapper = styled.a`
  width: 33%;
  height: 100%;
  position: relative;
  cursor: pointer;
  overflow: hidden;
  pointer-events: auto;
  z-index: 10;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    pointer-events: none;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    width: 100%;
    max-width: 420px;
    min-height: 150px;
    height: calc((100% - 16px) / 3);

    img {
      object-fit: contain;
      background: rgba(0, 0, 0, 0.04);
    }
  }
`;
